import moment from 'moment'
import { IDateProvider } from '../interface/daily-capitalisation'
import axios, { AxiosResponse, AxiosError } from 'axios'
import { logError } from './error-handler'

type GraphQLResponse<T> = {
  data: T
  errors?: unknown
}

class GraphQLError extends Error {
  constructor(
    public message: string,
    public url: string,
    public query: string,
    public statusCode?: number,
    public graphQLErrors?: unknown,
  ) {
    super(message)
    this.name = 'GraphQLError'
    Object.setPrototypeOf(this, GraphQLError.prototype)
  }
}

const isValidResponse = <T>(response: AxiosResponse<GraphQLResponse<T>>): boolean => {
  return (
    response.status >= 200 && response.status < 300 && response.data && response.data.data !== null
  )
}

const createGraphQLRequestConfig = (query: string) => ({
  method: 'POST' as const,
  headers: { 'Content-Type': 'application/json' },
  data: { query },
})

const handleAxiosError = (error: AxiosError, url: string, query: string): never => {
  if (error.response) {
    throw new GraphQLError(
      `HTTP error: ${error.response.status} ${error.response.statusText}`,
      url,
      query,
      error.response.status,
    )
  } else if (error.request) {
    throw new GraphQLError('No response received from the server', url, query)
  } else {
    throw new GraphQLError(`Error setting up the request: ${error.message}`, url, query)
  }
}

export const fetchGraphQLData = async <T>(url: string, query: string): Promise<T> => {
  try {
    const config = createGraphQLRequestConfig(query)
    const response = await axios<GraphQLResponse<T>>({ url, ...config })

    if (!isValidResponse(response)) {
      throw new GraphQLError(
        'Invalid GraphQL response',
        url,
        query,
        response.status,
        response.data.errors,
      )
    }

    if (response.data.errors) {
      logError(`GraphQL query returned errors: ${JSON.stringify(response.data.errors)}`)
    }

    return response.data.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleAxiosError(error, url, query)
    }
    if (error instanceof GraphQLError) {
      logError(error)
      throw error
    }
    throw new GraphQLError(`Unexpected error: ${(error as Error).message}`, url, query)
  }
}

export const fetchAllGraphQLDataWithOffset = async <T extends IDateProvider>(
  fetch: (now: number) => Promise<T[]>,
): Promise<T[]> => {
  let dataBuffer: T[] = []

  const getAllDataForProduct = async () => {
    let now = moment().unix()
    let dataset: T[] = []

    do {
      const response = await fetch(now)
      dataset = response
      if (dataset.length !== 0) {
        now = dataset[dataset.length - 1].date
        dataBuffer = dataBuffer.concat(dataset)
      }
    } while (dataset.length !== 0)

    return dataBuffer
  }

  return await getAllDataForProduct()
}
